import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Check, X, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

interface GoalTally {
  goalId: string;
  title: string;
  shortTitle: string;
  emoji: string;
  mandatory: boolean;
  days: Record<string, boolean | null>; // date -> true=done, false=missed, null=no data
}

interface TallyScreenProps {
  weeklyData: Array<{ date: string; completed: number; total: number; points: number }>;
  monthlyData: Array<{ date: string; completed: number; total: number; points: number }>;
  streak: number;
  goalTallies?: GoalTally[];
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function TallyScreen({ weeklyData, monthlyData, streak, goalTallies }: TallyScreenProps) {
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [viewYear, setViewYear] = useState(now.getFullYear());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const monthNames = ["Ocak", "Subat", "Mart", "Nisan", "Mayis", "Haziran", "Temmuz", "Agustos", "Eylul", "Ekim", "Kasim", "Aralik"];

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Generate dates for the month
  const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    return `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  });

  const todayStr = now.toISOString().split("T")[0];

  // Default mock tallies if not provided
  const tallies: GoalTally[] = goalTallies ?? generateMockTallies(monthDates);

  // Stats
  const totalCells = tallies.length * daysInMonth;
  const doneCells = tallies.reduce((sum, g) => sum + monthDates.filter(d => g.days[d] === true).length, 0);
  const missedCells = tallies.reduce((sum, g) => sum + monthDates.filter(d => g.days[d] === false).length, 0);
  const overallPct = totalCells > 0 ? Math.round((doneCells / (doneCells + missedCells || 1)) * 100) : 0;

  return (
    <div className="pb-24 px-2 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-2">
        <h1 className="text-xl font-bold">Cetele</h1>
        <Badge variant="warning">{streak} gun seri</Badge>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button onClick={prevMonth} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80">
          <ChevronLeft size={18} />
        </button>
        <h2 className="text-lg font-bold min-w-[140px] text-center">
          {monthNames[viewMonth]} {viewYear}
        </h2>
        <button onClick={nextMonth} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-4 px-2">
        <Card>
          <CardContent className="p-2 text-center">
            <p className="text-lg font-bold text-ehille-green">{doneCells}</p>
            <p className="text-[9px] text-muted-foreground">Yapildi ✓</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2 text-center">
            <p className="text-lg font-bold text-ehille-red">{missedCells}</p>
            <p className="text-[9px] text-muted-foreground">Yapilmadi ✗</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2 text-center">
            <p className={cn("text-lg font-bold", overallPct >= 70 ? "text-ehille-green" : overallPct >= 40 ? "text-gold" : "text-ehille-red")}>
              %{overallPct}
            </p>
            <p className="text-[9px] text-muted-foreground">Basari</p>
          </CardContent>
        </Card>
      </div>

      {/* ═══ MAIN TALLY TABLE ═══ */}
      <Card className="overflow-hidden mb-4">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: `${tallies.length * 44 + 48}px` }}>
              {/* Goal headers (columns) */}
              <thead>
                <tr className="bg-muted/60">
                  <th className="sticky left-0 z-20 bg-ehille-dark text-gold text-[10px] font-bold p-1.5 min-w-[42px] text-center border-r border-border">
                    Gun
                  </th>
                  {tallies.map(goal => (
                    <th key={goal.goalId} className="p-1 text-center min-w-[40px] border-r border-border/30">
                      <div className="text-sm leading-none">{goal.emoji}</div>
                      <div className={cn(
                        "text-[8px] font-bold mt-0.5 leading-tight",
                        goal.mandatory ? "text-gold" : "text-muted-foreground",
                      )}>
                        {goal.shortTitle}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {monthDates.map((date, dayIdx) => {
                  const dayNum = dayIdx + 1;
                  const isToday = date === todayStr;
                  const isFuture = date > todayStr;
                  const dayDone = tallies.filter(g => g.days[date] === true).length;
                  const dayTotal = tallies.filter(g => g.days[date] !== null).length;
                  const allDone = dayDone === dayTotal && dayTotal > 0;

                  return (
                    <tr key={date} className={cn(
                      "border-b border-border/30 transition-colors",
                      isToday && "bg-gold/10",
                      allDone && !isToday && "bg-ehille-green/5",
                      isFuture && "opacity-30",
                    )}>
                      {/* Day number */}
                      <td className={cn(
                        "sticky left-0 z-10 text-center p-1 border-r border-border font-bold text-xs",
                        isToday ? "bg-gold text-ehille-dark" : "bg-ehille-dark text-foreground",
                      )}>
                        {dayNum}
                      </td>

                      {/* Goal cells */}
                      {tallies.map(goal => {
                        const status = goal.days[date];
                        return (
                          <td key={goal.goalId} className="p-0.5 text-center border-r border-border/20">
                            {status === null || isFuture ? (
                              <div className="w-7 h-7 mx-auto" />
                            ) : status ? (
                              <div className="w-7 h-7 mx-auto rounded bg-ehille-green/15 flex items-center justify-center">
                                <Check size={14} className="text-ehille-green" strokeWidth={3} />
                              </div>
                            ) : (
                              <div className="w-7 h-7 mx-auto rounded bg-ehille-red/10 flex items-center justify-center">
                                <X size={13} className="text-ehille-red" strokeWidth={2.5} />
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              {/* Footer: per-goal totals */}
              <tfoot>
                <tr className="bg-muted/60 border-t-2 border-border">
                  <td className="sticky left-0 z-10 bg-ehille-dark text-gold text-[9px] font-bold p-1.5 text-center border-r border-border">
                    TOP
                  </td>
                  {tallies.map(goal => {
                    const done = monthDates.filter(d => goal.days[d] === true).length;
                    const missed = monthDates.filter(d => goal.days[d] === false).length;
                    const total = done + missed;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    return (
                      <td key={goal.goalId} className="p-1 text-center border-r border-border/30">
                        <div className={cn(
                          "text-[10px] font-bold",
                          pct >= 70 ? "text-ehille-green" : pct >= 40 ? "text-gold" : "text-ehille-red",
                        )}>
                          {done}/{total}
                        </div>
                        <div className="text-[8px] text-muted-foreground">%{pct}</div>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mb-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-ehille-green/15 flex items-center justify-center">
            <Check size={12} className="text-ehille-green" strokeWidth={3} />
          </div>
          Yapildi
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-ehille-red/10 flex items-center justify-center">
            <X size={11} className="text-ehille-red" strokeWidth={2.5} />
          </div>
          Yapilmadi
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-gold flex items-center justify-center text-[9px] font-bold text-ehille-dark">12</div>
          Bugun
        </span>
      </div>

      {/* Monthly bar chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp size={16} className="text-gold" />
            Gunluk Puan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between gap-[2px] h-32">
            {(weeklyData.length > 0 ? weeklyData : monthlyData.slice(0, 14)).map((day, i) => {
              const maxPts = Math.max(...(weeklyData.length > 0 ? weeklyData : monthlyData).map(d => d.points), 1);
              const height = maxPts > 0 ? (day.points / maxPts) * 100 : 0;
              const pct = day.total > 0 ? Math.round((day.completed / day.total) * 100) : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[7px] text-muted-foreground">{day.points > 0 ? day.points : ""}</span>
                  <div className="w-full relative" style={{ height: "90px" }}>
                    <div
                      className={cn(
                        "absolute bottom-0 w-full rounded-t transition-all duration-500",
                        pct >= 80 ? "bg-ehille-green" : pct >= 50 ? "bg-gold" : "bg-ehille-red/60",
                      )}
                      style={{ height: `${height}%`, minHeight: day.points > 0 ? "3px" : "0" }}
                    />
                  </div>
                  <span className="text-[8px] text-muted-foreground">{day.date.slice(8)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Mock tally generator ─────────────────────────────────
function generateMockTallies(dates: string[]): GoalTally[] {
  const today = new Date().toISOString().split("T")[0];
  const goals = [
    { goalId: "g1", title: "Kuran Okuma", shortTitle: "Kuran", emoji: "📖", mandatory: true },
    { goalId: "g2", title: "Meal Okuma", shortTitle: "Meal", emoji: "📕", mandatory: true },
    { goalId: "g3", title: "Hadis Okuma", shortTitle: "Hadis", emoji: "📗", mandatory: true },
    { goalId: "g4", title: "Siyer Okuma", shortTitle: "Siyer", emoji: "📘", mandatory: true },
    { goalId: "g5", title: "Vird / Zikir", shortTitle: "Vird", emoji: "🤲", mandatory: true },
    { goalId: "g6", title: "Sabah Namazi", shortTitle: "Sabah", emoji: "🕌", mandatory: true },
    { goalId: "g7", title: "Ogle Namazi", shortTitle: "Ogle", emoji: "🕌", mandatory: true },
    { goalId: "g8", title: "Ikindi Namazi", shortTitle: "Ikindi", emoji: "🕌", mandatory: true },
    { goalId: "g9", title: "Aksam Namazi", shortTitle: "Aksam", emoji: "🕌", mandatory: true },
    { goalId: "g10", title: "Yatsi Namazi", shortTitle: "Yatsi", emoji: "🕌", mandatory: true },
    { goalId: "g11", title: "30dk Spor", shortTitle: "Spor", emoji: "🏃", mandatory: false },
    { goalId: "g12", title: "2L Su Ic", shortTitle: "Su", emoji: "💧", mandatory: false },
  ];

  // Seed-based pseudo-random for consistent data
  const seed = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };

  return goals.map(g => ({
    ...g,
    days: Object.fromEntries(dates.map(date => {
      if (date > today) return [date, null];
      const hash = seed(g.goalId + date);
      // Mandatory goals have higher completion rate
      const threshold = g.mandatory ? 0.75 : 0.55;
      return [date, (hash % 100) / 100 < threshold];
    })),
  }));
}
